import * as React from 'react';
import { DefaultButton, PrimaryButton, mergeStyleSets, IDropdownOption, Stack } from '@fluentui/react';

type Key = string | number;

interface CategoryButtonsProps {
  value?: Key; // selectedKey
  options: IDropdownOption[]; // props.categorias
  onSelect: (key: string) => void; // onChange(CRITERIO_CATEGORIA, key)
  clubHasCategoria: (key: string) => boolean | undefined; // habilitar/deshabilitar
  clubHasCategoriaName: (key: string, text: string) => string; // formatea el texto
  label?: string; // aria-label
  renderOptionCategoria?: (opt: IDropdownOption, selected: boolean) => React.ReactNode; // opcional
  fullWidth?: boolean; // opcional: 100% ancho del contenedor
  gap?: number; // opcional: espacio vertical entre botones (default 8)
}

const classes = mergeStyleSets({
  // contenedor en columna
  column: { display: 'flex', flexDirection: 'column' },
  // botón ancho y con esquinas suaves
  btn: { borderRadius: 2, border: '1px solid #f1f1f1ff', textAlign: 'left' },
});

const CategoryButtons: React.FC<CategoryButtonsProps> = ({
  value,
  options,
  onSelect,
  clubHasCategoria,
  clubHasCategoriaName,
  label = 'Categoría',
  renderOptionCategoria,
  fullWidth = true,
  gap = 8,
}) => {
  // Flechas ↑/↓ para moverse por opciones habilitadas
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const enabled = options.filter((o) => clubHasCategoria(String(o.key)));
    if (!enabled.length) return;
    const idx = enabled.findIndex((o) => String(o.key) === String(value));
    let next: IDropdownOption | undefined;

    if (e.key === 'ArrowDown') next = enabled[(idx + 1 + enabled.length) % enabled.length];
    if (e.key === 'ArrowUp') next = enabled[(idx - 1 + enabled.length) % enabled.length];

    if (next) {
      onSelect(String(next.key));
      e.preventDefault();
    }
  };

  return (
    <Stack
      role="radiogroup"
      aria-label={label}
      className={classes.column}
      tokens={{ childrenGap: gap }}
      onKeyDown={onKeyDown}
    >
      {options.map((opt, i) => {
        const k = String(opt.key);
        const selected = String(value) === k;
        const disabled = !clubHasCategoria(k);
        const Btn = selected ? PrimaryButton : DefaultButton;

        const text = clubHasCategoriaName(k, opt.text ?? String(opt.key));

        return (
          <Btn
            key={k}
            className={classes.btn}
            onClick={() => onSelect(k)}
            disabled={disabled}
            role="radio"
            aria-checked={selected}
            aria-pressed={selected}
            autoFocus={i === 0}
            text={text}
            styles={{
              root: {
                width: fullWidth ? '100%' : undefined,
                justifyContent: 'flex-start',
                height: '42px',
              },
              // asegura el alineado del contenido a la izquierda
              flexContainer: { justifyContent: 'flex-start' },
            }}
            // si querés reusar tu onRenderOptionCategoria:
            onRenderText={
              renderOptionCategoria
                ? (buttonProps, defaultRender) => (
                    <>{renderOptionCategoria({ ...opt, text }, selected) ?? defaultRender?.(buttonProps)}</>
                  )
                : undefined
            }
          />
        );
      })}
    </Stack>
  );
};

export default CategoryButtons;
